import React, {useEffect, useState} from "react";
import {NetworkCalls} from "../../NetworkCalls";
import LoadingPageIndicator from "../../components/LoadingPageIndicator";
import {Inspection} from "../../dtos/Inspection";
import uuid from "react-uuid";
import TimestampFormat from "../../utils/TimestampFormat";
import axios from "axios";
import {NetworkErrorHandling} from "../../components/NetworkErrorHandling";
import { useNavigate } from "react-router-dom";
import {DownloadExcelCsvButton} from "../../components/DownloadExcelCsvButton";
import {useSelectedOrganisationContext} from "../../custom-hooks/useOrganisation";
import { Dropdown } from "react-bootstrap";

export default function InspectionAreaOverview() {
    const navigate = useNavigate();
    const [loading, isLoading] = useState<boolean>(false);
    const [inspectionCalendarYears, setInspectionCalendarYears] = useState<
        number[]
    >([]);
    const [inspections, setInspections] = useState<Inspection[]>([]);
    const [selectedYear, setSelectedYear] = useState<number>(-1);
    const [excelDownloadURL, setExcelDownloadURL] = useState<string>("");

    // Pagination
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(25);

    // Search
    const [searchQuery, setSearchQuery] = useState<string>("");

    const { selectedOrganisation } = useSelectedOrganisationContext();

    useEffect(() => {
        isLoading(true);
        NetworkCalls.inspectionReportedYears()
            .then((result) => {
                const inspectionCalendarYears = result.sort(sortByYear);
                setInspectionCalendarYears(inspectionCalendarYears);
                setSelectedYear(inspectionCalendarYears[0]);
            })
            .catch((error) => {
                isLoading(false);
                NetworkErrorHandling.handle(error);
            });
    }, []);

    useEffect(() => {
        setInspections([]);

        if (!selectedOrganisation) {
            return;
        }

        isLoading(true);
        setCurrentPage(1); // Reset to first page when year changes

        setExcelDownloadURL(
            `${axios.defaults.baseURL}/inspection/inspection-list-excel?organisationId=${selectedOrganisation.id}&year=${selectedYear}`,
        );
        NetworkCalls.listInspections(selectedYear)
            .then((res) => {
                setInspections(res.sort(sortByInspectionTime));
                isLoading(false);
            })
            .catch((error) => {
                isLoading(false);
                NetworkErrorHandling.handle(error);
            });
    }, [selectedYear, selectedOrganisation]);

    // Filter by search query
    const filteredData = inspections.filter((item) => {
        if (!searchQuery.trim()) return true;
        const userNumber = item.userNumber?.toLowerCase() || '';
        const firstName = item.firstName?.toLowerCase() || '';
        const lastName = item.lastName?.toLowerCase() || '';
        const waterName = item.water?.waterName?.toLowerCase() || '';
        const query = searchQuery.toLowerCase();
        return userNumber.includes(query) || firstName.includes(query) || lastName.includes(query) || waterName.includes(query);
    });

    // Pagination calculations
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    function sortByYear(a: number, b: number) {
        return b - a;
    }

    function sortByInspectionTime(a: Inspection, b: Inspection) {
        return b.inspectionTime - a.inspectionTime;
    }

    const options = inspectionCalendarYears.map((item) => {
        return (
            <option key={item} value={item}>
                {item}
            </option>
        );
    });

    function changeYear(selectedYear: string) {
        setSelectedYear(Number(selectedYear));
    }

    function showContent() {
        if (!selectedOrganisation) {
            return (<>Zuerst Organisation auswählen</>);
        }
        return (
            <div className="container-fluid px-0" style={{ height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
                <div className="row g-0 h-100">
                    {/* Filter Sidebar */}
                    <div className="col-12 col-md-3 col-lg-2 bg-light border-end h-100" style={{ overflowY: 'auto' }}>
                        <div className="p-3">
                            <h5 className="mb-3">Filter</h5>

                            <div className="mb-4">
                                <label htmlFor="yearControl" className="form-label fw-semibold">Jahr</label>
                                <select
                                    id="yearControl"
                                    className="form-select"
                                    onChange={(elem) => changeYear(elem.target.value)}
                                    defaultValue={selectedYear}
                                >
                                    {options}
                                </select>
                            </div>

                            <div className="mb-4">
                                <label className="form-label fw-semibold">Excel Download</label>
                                <DownloadExcelCsvButton downloadUrl={excelDownloadURL} />
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="col-12 col-md-9 col-lg-10 h-100" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <div className="p-3 h-100 d-flex flex-column" style={{ minHeight: 0 }}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="m-0">Fischereiaufsicht {selectedYear}</h4>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="input-group input-group-sm" style={{ width: '250px' }}>
                                        <span className="input-group-text bg-white">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="11" cy="11" r="8"></circle>
                                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                            </svg>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Suche..."
                                            value={searchQuery}
                                            onChange={(e) => {
                                                setSearchQuery(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        />
                                        {searchQuery && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => {
                                                    setSearchQuery("");
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: 'auto' }}
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value={10}>10 pro Seite</option>
                                        <option value={25}>25 pro Seite</option>
                                        <option value={50}>50 pro Seite</option>
                                        <option value={100}>100 pro Seite</option>
                                    </select>
                                </div>
                            </div>

                            <div className="card shadow-sm flex-grow-1" style={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                <div className="table-responsive flex-grow-1" style={{ overflowY: 'auto' }}>
                                    {showInspectionTable()}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="card-footer bg-white border-top d-flex justify-content-between align-items-center py-2">
                                        <span className="text-muted small">
                                            Zeige {startIndex + 1} - {Math.min(endIndex, filteredData.length)} von {filteredData.length}
                                        </span>
                                        <nav>
                                            <ul className="pagination pagination-sm mb-0">
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(1)}
                                                        disabled={currentPage === 1}
                                                    >
                                                        «
                                                    </button>
                                                </li>
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                        disabled={currentPage === 1}
                                                    >
                                                        ‹
                                                    </button>
                                                </li>
                                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                    let pageNum: number;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    return (
                                                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => setCurrentPage(pageNum)}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        ›
                                                    </button>
                                                </li>
                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(totalPages)}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        »
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function showInspectionTable() {
        return (
            <table className="table table-hover table-striped mb-0">
                <thead className="table-light sticky-top">
                <tr key={uuid()}>
                    <th scope="col"></th>
                    <th scope="col">Aufseher</th>
                    <th scope="col">Datum</th>
                    <th scope="col">Gewässer</th>
                    <th scope="col">Mitgliedsnummer</th>
                    <th scope="col">Bemerkung</th>
                        <th scope="col" style={{ width: '60px' }}></th>
                </tr>
                </thead>
                <tbody id="table-content">{createRows()}</tbody>
            </table>
        );
    }

    function createRows() {
        return paginatedData.map((elem) => {
            return (
                <tr key={uuid()}>
                    <td>{showAttachment(elem)}</td>
                    <td>
                        {" "}
                        {elem.firstName} {elem.lastName}
                    </td>
                    <td>
                        {TimestampFormat.toDateTimeReadableFormat(elem.inspectionTime)}
                    </td>
                    <td>{elem.water.waterName}</td>
                    <td>{elem.userNumber}</td>
                    <td>{elem.description}</td>
                    <td>
                        <Dropdown align="end">
                            <Dropdown.Toggle
                                variant="link"
                                className="btn btn-link p-1 text-decoration-none text-primary fs-5 lh-1"
                                style={{ fontSize: '1.5rem' }}
                            >
                                ⋮
                            </Dropdown.Toggle>
                            <Dropdown.Menu style={{ backgroundColor: "#fff", zIndex: 1060 }}>
                                <Dropdown.Item
                                    onClick={() => navigate(`/dashboard/inspections/details/${elem.id}`)}
                                >
                                    Details anzeigen
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </td>
                </tr>
            );
        });
    }

    function showAttachment(inspection: Inspection) {
        if (inspection.hasPhoto) {
            return (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon icon-tabler icon-tabler-files"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M15 3v4a1 1 0 0 0 1 1h4"></path>
                    <path d="M18 17h-7a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h4l5 5v7a2 2 0 0 1 -2 2z"></path>
                    <path d="M16 17v2a2 2 0 0 1 -2 2h-7a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h2"></path>
                </svg>
            );
        } else {
            return;
        }
    }

    return <div>{loading ? <LoadingPageIndicator/> : showContent()}</div>;
}
